import { CommanderActionDef } from "./redemptionActions";

/**
 * Daily Tasks dashboard actions.
 * `action_id` keys are stable; `name` / `description` are what GA users read.
 */
export const DAILY_TASKS_ACTIONS = {
  dashboard_view: {
    name: "View Tasks Dashboard",
    description:
      "User opened Daily Tasks and loaded TAT summary plus the work queue",
  },
  filter_apply: {
    name: "Apply Filters",
    description:
      "User clicked Apply / Search and refreshed Daily Tasks with current filters",
  },
  filter_clear: {
    name: "Clear Filters",
    description:
      "User cleared Daily Tasks filters back to defaults (current stage kept)",
  },
  stage_tab_change: {
    name: "Change Stage Tab",
    description:
      "User switched Daily Tasks stage tab (Projects, Mockup, PR, PO, …)",
  },
  date_preset_select: {
    name: "Select Date Preset",
    description:
      "User picked a quick date chip (Last 7 / 30 / 90 days, 6 months, or FY) before Apply",
  },
  date_range_change: {
    name: "Pick Custom Dates",
    description: "User changed the Daily Tasks custom date range (draft, before Apply)",
  },
  search_panel_toggle: {
    name: "Toggle Filters Panel",
    description: "User expanded or collapsed the Daily Tasks filters panel",
  },
  filter_order_type_change: {
    name: "Change Order Type",
    description: "User changed order type filter (draft, before Apply)",
  },
  filter_order_id_change: {
    name: "Change Order ID",
    description: "User changed order ID filter (draft, before Apply)",
  },
  filter_customer_change: {
    name: "Change Customer",
    description: "User changed customer filter (draft, before Apply)",
  },
  filter_pm_poc_change: {
    name: "Change Project POC",
    description: "User changed Project POC filter (draft, before Apply)",
  },
  filter_sales_poc_change: {
    name: "Change Sales POC",
    description: "User changed Sales POC filter (draft, before Apply)",
  },
  filter_project_status_change: {
    name: "Change Project Status",
    description: "User changed Active/Archived/Both project status (draft)",
  },
  filter_excluded_orders_change: {
    name: "Change Excluded Orders",
    description:
      "User toggled whether excluded-from-sourcing orders are included (draft)",
  },
  filter_include_running_change: {
    name: "Change Include Running",
    description:
      "User toggled include running orders in TAT calculations (draft)",
  },
  filter_date_field_change: {
    name: "Change Date Field",
    description:
      "User changed which date field the range applies to (draft, inward stages)",
  },
  work_tile_select: {
    name: "Select Work Tile",
    description:
      "User clicked a named open-work or urgency tile to filter the queue",
  },
  queue_sort: {
    name: "Sort Work Queue",
    description: "User sorted a column in the Daily Tasks work queue table",
  },
  queue_page_change: {
    name: "Change Queue Page",
    description: "User changed page on the Daily Tasks work queue",
  },
  queue_page_size_change: {
    name: "Change Queue Rows Per Page",
    description: "User changed rows per page on the work queue",
  },
  drilldown_open: {
    name: "Open Metric Card",
    description:
      "User opened a clickable Daily Tasks card (ledger, stage, or activity)",
  },
  drilldown_close: {
    name: "Close Metric Card",
    description: "User closed the Daily Tasks card drilldown drawer",
  },
  drilldown_scope_change: {
    name: "Change Card Scope",
    description:
      "User switched Measured / Open / All scope inside an open card",
  },
  drilldown_sort: {
    name: "Sort Card Table",
    description: "User sorted a column inside an open Daily Tasks card",
  },
  drilldown_page_change: {
    name: "Change Card Page",
    description: "User changed page inside an open Daily Tasks card table",
  },
  drilldown_page_size_change: {
    name: "Change Card Rows Per Page",
    description: "User changed rows per page inside an open Daily Tasks card",
  },
  export_csv: {
    name: "Export Card CSV",
    description: "User exported CSV for the currently open Daily Tasks card",
  },
  formula_toggle: {
    name: "Toggle Formula Help",
    description:
      "User expanded or collapsed How this is calculated on an open card",
  },
  product_drawer_open: {
    name: "Open Product Detail",
    description:
      "User opened the product detail drawer from the queue or drilldown",
  },
  product_drawer_close: {
    name: "Close Product Detail",
    description: "User closed the Daily Tasks product detail drawer",
  },
  link_order_open: {
    name: "Open Order",
    description: "User opened a project/order link from Daily Tasks",
  },
  link_product_open: {
    name: "Open Product",
    description: "User opened a product link from Daily Tasks",
  },
} as const satisfies Record<string, CommanderActionDef>;

export type DailyTasksActionId = keyof typeof DAILY_TASKS_ACTIONS;
